import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, InputAdornment, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { Alert } from '@app/ui/alert';
import { Loader } from '@app/ui/loader';
import { Text } from '@app/ui/forms';
import { error as showError, warning as showWarning } from '@app/snackbars';

import { MAX_SELECTED_IPS, MATCHING_LOADER_ROUTE, getTierDisplayOrder } from '../constants';
import { MATCHING_RUN_START } from '../gql';
import { matchingSession } from '../session';
import { useMatchingPool, MatchingPoolItem } from '../hooks/useMatchingPool';
import { useMatchingSession } from '../hooks/useMatchingSession';
import { formatLocation } from '../utils/format-location';

// Stable sort, `null` last — no secondary key is specced anywhere; the pool already arrives sorted
// (02 AC-2), this is a harmless no-op safety.
const sortGroup = (items: MatchingPoolItem[]): MatchingPoolItem[] =>
  [...items].sort((a, b) => {
    if (!a.startSurroSearch && !b.startSurroSearch) {
      return 0;
    }
    if (!a.startSurroSearch) {
      return 1;
    }
    if (!b.startSurroSearch) {
      return -1;
    }

    return new Date(a.startSurroSearch).getTime() - new Date(b.startSurroSearch).getTime();
  });

const matchesSearch = (query: string) => (item: MatchingPoolItem) =>
  !query || item.displayName.toLowerCase().includes(query.toLowerCase());

interface MatchingRunStartResult {
  matchingRunStart: {
    runId: string;
    status: string;
    droppedQuestionnaireIds: number[] | null;
  };
}

export const MatchingSelectionScreen = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { pool, loading, error: poolError, refetch } = useMatchingPool();
  const { selectedIds, toggleId, selectGroup, deselectGroup, clearSelection, dropIds, setRunId } = useMatchingSession();
  const [search, setSearch] = useState('');
  const [droppedCount, setDroppedCount] = useState(0);
  const [matchingRunStart, { loading: runLoading }] = useMutation<MatchingRunStartResult>(MATCHING_RUN_START);
  // Apollo's `loading` flips only after a re-render — two clicks in one tick both pass it (AC-8: fires once).
  const runInFlight = useRef(false);

  // Re-entry reconciliation (§4.7). Reads the session singleton, not `selectedIds`, so a stale
  // closure can't matter. A failed fetch yields `pool = []` — without the `poolError` guard it
  // would wipe the whole selection (AC-8 says an error leaves it intact).
  useEffect(() => {
    if (loading || poolError) {
      return;
    }

    const deliveredIds = new Set(pool.map(item => item.questionnaireId));
    const dropped = matchingSession.selectedQuestionnaireIds.filter(id => !deliveredIds.has(id));

    if (dropped.length > 0) {
      dropIds(dropped);
      setDroppedCount(dropped.length);
    }
  }, [pool, loading, poolError]);

  const atCap = selectedIds.length >= MAX_SELECTED_IPS;
  const isEmptyPool = !loading && !poolError && pool.length === 0;
  const runDisabled =
    runLoading || !!poolError || isEmptyPool || selectedIds.length < 1 || selectedIds.length > MAX_SELECTED_IPS;

  const handleRun = async () => {
    if (runInFlight.current) {
      return;
    }
    runInFlight.current = true;

    try {
      const { data } = await matchingRunStart({ variables: { input: { questionnaireIds: selectedIds } } });
      const run = data?.matchingRunStart;

      if (run) {
        setRunId(run.runId);

        if (run.droppedQuestionnaireIds?.length) {
          dropIds(run.droppedQuestionnaireIds);
          showWarning(t('matching.notes.droppedFromPool', { count: run.droppedQuestionnaireIds.length }) ?? '');
        }

        navigate(MATCHING_LOADER_ROUTE);
      }
    } catch {
      showError(t('matching.run.error') ?? '');
      refetch().catch(() => undefined);
    } finally {
      runInFlight.current = false;
    }
  };

  return (
    <Box>
      {/* Childless = overlay only; wrapping the form would unmount the selection on a failed start. */}
      <Loader isLoading={runLoading} />

      <Typography variant="h1" sx={{ mb: 3 }}>
        {t('matching.page.title')}
      </Typography>

      {droppedCount > 0 && (
        <Alert severity="warning" onClose={() => setDroppedCount(0)} sx={{ mb: 2 }}>
          {t('matching.notes.droppedFromPool', { count: droppedCount })}
        </Alert>
      )}

      {/* Design values (EVIDENCE Phase B table): card r20/p32, count bar r20 `16px 24px`, 3-col grids gap 16. */}
      <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'grey.500', borderRadius: 5, p: 4 }}>
        <Typography variant="h2">{t('matching.card.title')}</Typography>
        <Typography variant="subtitle1" component="p" sx={{ mt: 1 }}>
          {t('matching.card.subtitle')}
        </Typography>

        <Box
          sx={{
            mt: 3,
            py: 2,
            px: 3,
            bgcolor: 'primaryLight.light',
            borderRadius: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h4" color="text.secondary">
            {t('matching.selection.count', { count: selectedIds.length, max: MAX_SELECTED_IPS })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isEmptyPool && (
              <Typography variant="body1" color="text.secondary">
                {t('matching.run.emptyPool')}
              </Typography>
            )}
            <Button variant="contained" disabled={runDisabled} onClick={handleRun}>
              {t('matching.run.button')}
            </Button>
            <Button variant="text" disabled={selectedIds.length === 0} onClick={clearSelection}>
              {t('matching.selection.clear')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
          <Text
            label={t('matching.search.label')}
            placeholder={t('matching.search.placeholder') ?? undefined}
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Loader isLoading={loading}>
          {poolError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {t('general.error.somethingWentWrong')}
            </Alert>
          )}

          {!poolError && (
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              {getTierDisplayOrder().map(tier => {
                const groupItems = sortGroup(pool.filter(item => item.tier === tier));
                const visibleItems = groupItems.filter(matchesSearch(search));
                const visibleIds = visibleItems.map(item => item.questionnaireId);
                const anySelected = visibleIds.some(id => selectedIds.includes(id));
                const toggleDisabled = !anySelected && (visibleIds.length === 0 || atCap);

                return (
                  <Box
                    key={tier}
                    sx={{
                      border: 1,
                      borderColor: 'grey.500',
                      borderRadius: 5,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.25,
                        py: 2,
                        px: 2.5,
                      }}
                    >
                      {/* 18px has no typography token (h3 is 20 at sm) — design value, one-off. */}
                      <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.4 }}>
                        {`${t(`matching.groups.${tier}`)} (${groupItems.length})`}
                      </Typography>
                      <Button
                        variant="text"
                        disabled={toggleDisabled}
                        sx={{ flexShrink: 0 }}
                        onClick={() =>
                          anySelected ? deselectGroup(visibleIds) : selectGroup(visibleIds, MAX_SELECTED_IPS)
                        }
                      >
                        {t(anySelected ? 'matching.selection.deselectAll' : 'matching.selection.selectAll')}
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        height: 349,
                        overflowY: 'auto',
                        px: 2.5,
                        scrollbarWidth: 'thin',
                        scrollbarColor: theme => `${theme.palette.primary.main} transparent`,
                      }}
                    >
                      {visibleItems.length === 0 ? (
                        <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                          {t('matching.groups.empty')}
                        </Typography>
                      ) : (
                        visibleItems.map((item, index) => (
                          <Box
                            key={item.questionnaireId}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '18px minmax(0, 1fr)',
                              gap: 1,
                              alignItems: 'start',
                              py: 1.75,
                              height: 70,
                              boxSizing: 'border-box',
                              borderBottom: 1,
                              borderColor: index === visibleItems.length - 1 ? 'transparent' : 'grey.500',
                            }}
                          >
                            {/* Raw MUI Checkbox, not the `@app/ui/forms` wrapper: the label is a two-line column
                                beside the box, and the wrapper's FormControlLabel would make it the accessible name. */}
                            <Checkbox
                              checked={selectedIds.includes(item.questionnaireId)}
                              disabled={atCap && !selectedIds.includes(item.questionnaireId)}
                              onChange={() => toggleId(item.questionnaireId)}
                              inputProps={{ 'aria-label': item.displayName }}
                              // The design's `ml: -1px` paints the icon 8px right, into the gap; -10px keeps it in its 18px column.
                              sx={{ fontSize: 18, p: '9px', m: '-9px', ml: '-10px' }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h5" noWrap>
                                {item.displayName}
                              </Typography>
                              <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                                noWrap
                                sx={{ lineHeight: 1.5 }}
                              >
                                {formatLocation(item.state, item.country) || t('matching.row.noLocation')}
                              </Typography>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Loader>
      </Box>
    </Box>
  );
};
